import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import SwipeableViews from 'react-swipeable-views';
import styled from 'styled-components';

import useWindowSize from '@/hooks/useWindowSize';
import { FontStyle } from '@/utils/StyleUtil';
import { LIST_ITEM_PAGE } from '@/models';
import DividerDefault from '../Divider/Default';
import Image from 'next/future/image';

const TABBAR_HEIGHT = 57;

export interface IScene {
    key: string | number;
    label: string;
    page: (_props: { index: number; currentTab: number; saveScroll: () => void }) => React.ReactElement;
    numOfPlans?: number;
}

interface IProps {
    tab: number;
    onChangeTab: Function;
    renderScene: IScene[];
    flexTabBar?: boolean;
    isLoadDataMore?: boolean;
    activeColor: string;
    activeSubColor?: string;
    className?: string;
    containerStyle?: CSSProperties;
    tabBarPosition?: number;
    tooltip?: boolean;
    showListTooltip?: boolean;
    setShowListTooltip?: any;
}

const SwipeableTabView = ({
    tab,
    onChangeTab,
    renderScene,
    flexTabBar = true,
    isLoadDataMore,
    activeColor,
    activeSubColor,
    className,
    containerStyle,
    tabBarPosition = 56,
    tooltip = false,
    showListTooltip = false,
    setShowListTooltip = () => {
        return;
    },
}: IProps) => {
    const router = useRouter();
    const dividerRef = useRef<HTMLDivElement>(null);
    const swipeableRef = useRef<any>();
    const refs = useRef<HTMLDivElement[]>([]).current;
    const tabbarContainerRef = useRef<HTMLDivElement>(null);
    const tabbuttonRefs = useRef<HTMLButtonElement[]>([]).current;
    const { height } = useWindowSize();

    // TODO 원래 여기에 recoil 넣으면 안됨
    // const selIns = useRecoilValue(SelectedIns);
    // const themeColor = useRecoilValue(themeColorState(selIns));
    // const themeSubColor = useRecoilValue(themeSubColorState(selIns));

    const [dividerLen, setDividerLen] = useState(0);
    const [contentHeight, setContentHeight] = useState(height - swipeableRef.current?.offsetTop - TABBAR_HEIGHT);

    // const [showListTooltip, setShowListTooltip] = useState(false);

    useEffect(() => {
        const prev = window.sessionStorage.getItem('prevPage');

        if (prev && !LIST_ITEM_PAGE.includes(prev)) window.localStorage.removeItem(`${router.pathname}-scroll-y`);
        if (dividerRef.current !== null) setDividerLen(dividerRef.current.offsetWidth);
    }, []);

    // useEffect(() => {
    //     if (tooltip) {
    //         const storedShowListTooltip = localStorage.getItem('showListTooltip');
    //
    //         if (storedShowListTooltip) {
    //             setShowListTooltip(storedShowListTooltip === 'true');
    //         } else {
    //             localStorage.setItem('showListTooltip', 'true');
    //             setShowListTooltip(true);
    //         }
    //     }
    // }, []);

    const moveScreenToSavedScrollY = (currentTab?: number) => {
        if (typeof window !== 'undefined') {
            const scrollYStr = window.localStorage.getItem(`${router.pathname}-scroll-y`);

            if (scrollYStr && currentTab !== undefined) {
                const scrollYList = JSON.parse(scrollYStr);

                window.scrollTo({
                    left: 0,
                    top: scrollYList[currentTab],
                    behavior: 'auto',
                });
            }
        }
    };

    const saveTabScrollY = (currentTab: number) => {
        if (typeof window !== 'undefined') {
            const scrollYStr = window.localStorage.getItem(`${router.pathname}-scroll-y`);
            const scrollYList = scrollYStr ? JSON.parse(scrollYStr) : new Array(renderScene.length).fill(0);

            scrollYList[currentTab] = window.scrollY;
            window.localStorage.setItem(`${router.pathname}-scroll-y`, JSON.stringify(scrollYList));
        }
    };

    const onChangeCurrTab = (prevTab: number, currTab: number) => {
        saveTabScrollY(prevTab);

        router.replace(
            {
                pathname: `${router.pathname}`,
                query: {
                    category: currTab,
                },
            },
            undefined,
            { shallow: true },
        );

        onChangeTab(currTab);
    };

    useEffect(() => {
        setTimeout(() => {
            moveScreenToSavedScrollY(tab);
        }, 100);
    }, [tab]);

    useEffect(() => {
        if (refs[tab] && swipeableRef.current) {
            const currHeight = Math.max(
                refs[tab]?.offsetHeight,
                height - swipeableRef.current?.offsetTop - TABBAR_HEIGHT,
            );

            setContentHeight(currHeight);
        }
    }, [renderScene[tab], isLoadDataMore]);

    useEffect(() => {
        if (tabbarContainerRef.current) {
            const scrollY =
                tabbuttonRefs.slice(0, tab - 1).reduce((acc, cur) => acc + cur.offsetWidth, 0) +
                tabbuttonRefs[tab].offsetWidth / 2;
            if (tab === 0) tabbarContainerRef.current.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            else tabbarContainerRef.current.scrollTo({ top: 0, left: scrollY, behavior: 'smooth' });
        }
    }, [tab, tabbuttonRefs]);

    return (
        <Container className={className} style={containerStyle} ref={swipeableRef}>
            <TabBarContainer data-cy="swipeable__tabBar" top={tabBarPosition} ref={tabbarContainerRef}>
                <TabBarItems ref={dividerRef}>
                    {renderScene.map((scene, index: number) => (
                        <TabBarItem
                            ref={(node: HTMLButtonElement) => (tabbuttonRefs[index] = node)}
                            label={scene.label}
                            key={scene.key}
                            active={tab === index}
                            // activeColor={activeColor === undefined ? themeColor : activeColor}
                            activeColor={activeColor}
                            // activeSubColor={activeSubColor === undefined ? themeSubColor : activeSubColor}
                            activeSubColor={activeSubColor}
                            flexTabBar={flexTabBar}
                            onClick={() => {
                                onChangeCurrTab(tab, index);
                                if (renderScene[index].numOfPlans === 0) {
                                    setShowListTooltip(false);
                                } else {
                                    if (tooltip) {
                                        const storedShowListTooltip = localStorage.getItem('showListTooltip');

                                        if (storedShowListTooltip) {
                                            setShowListTooltip(storedShowListTooltip === 'true');
                                        } else {
                                            localStorage.setItem('showListTooltip', 'true');
                                            setShowListTooltip(true);
                                        }
                                    }
                                }
                            }}
                        >
                            <div className="label">
                                {scene.label}
                                {typeof scene.numOfPlans !== 'undefined' && (
                                    <div className="badge">{scene.numOfPlans}</div>
                                )}
                            </div>
                            {tab === index ? (
                                <DividerDefault
                                    thickness={2}
                                    color={activeColor}
                                    length="100%"
                                    style={{ borderRadius: '1px' }}
                                />
                            ) : undefined}
                        </TabBarItem>
                    ))}
                </TabBarItems>
                <DividerDefault
                    thickness={1}
                    color="#dadada"
                    length={flexTabBar ? '100%' : `${dividerLen}px`}
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        zIndex: -1,
                    }}
                />
            </TabBarContainer>

            <SwipeableViews
                index={tab}
                onChangeIndex={(index: number) => {
                    onChangeCurrTab(tab, index);
                    if (renderScene[index].numOfPlans === 0) {
                        setShowListTooltip(false);
                    } else {
                        if (tooltip) {
                            const storedShowListTooltip = localStorage.getItem('showListTooltip');

                            if (storedShowListTooltip) {
                                setShowListTooltip(storedShowListTooltip === 'true');
                            } else {
                                localStorage.setItem('showListTooltip', 'true');
                                setShowListTooltip(true);
                            }
                        }
                    }
                }}
                enableMouseEvents
                containerStyle={{ height: contentHeight, backgroundColor: '#ffffff' }}
            >
                {renderScene.map((scene, index: number) => {
                    return (
                        <div ref={(node: HTMLDivElement) => (refs[index] = node)} key={scene.key} style={{ zIndex: 9 }}>
                            {tooltip && showListTooltip && (
                                <>
                                    <CloseImage
                                        onClick={() => {
                                            localStorage.setItem('showListTooltip', 'false');
                                            setShowListTooltip(false);
                                        }}
                                    />
                                    <GifImage
                                        src="/img/plan/insurance-menu-tooltip-cat.gif"
                                        width={42}
                                        height={42}
                                        unoptimized={true}
                                        quality={100}
                                    />
                                    <TooltipImage
                                        src="/img/plan/insurance-menu-tooltip.png"
                                        width={200}
                                        height={56}
                                        unoptimized={true}
                                        quality={100}
                                    />
                                </>
                            )}
                            {scene.page({ index, currentTab: tab, saveScroll: () => saveTabScrollY(tab) })}
                        </div>
                    );
                })}
            </SwipeableViews>
        </Container>
    );
};

const Container = styled.div`
    /* div[aria-hidden='true'] {
        display: none;
    } */
    height: 100%;
    /* background-color: #ffffff; */
`;

const TabBarContainer = styled.div<{ top?: number }>`
    ${({ theme }) => theme.flex.flexOnly};
    position: -webkit-sticky;
    position: sticky;
    top: ${({ top }) => `${top}px`};
    z-index: 1;
    background-color: #ffffff;
    ${({ theme }) => theme.settingByBrowser.hideScrollBarX}
`;

const TabBarItems = styled.div`
    ${({ theme }) => theme.flex.flexOnly};
    flex: 1;
    position: relative;
    padding: 0 20px;
`;

const TabBarItem = styled.button<{
    active: boolean;
    flexTabBar: boolean;
    activeColor: string;
    activeSubColor?: string;
    label: string;
}>`
    position: relative;
    flex: ${({ flexTabBar }) => (flexTabBar ? 1 : 0)};
    .label {
        height: 44px;
        white-space: ${({ label }) => {
            if (label.includes('\n')) return undefined;
            return 'nowrap';
        }};
        padding: ${({ label }) => {
            if (
                label === '해약환급금\n일부지급형' ||
                label === '해약환급금\n미지급형' ||
                label === '해약환급금\n일부 ∙ 미지급형'
            )
                return '4px 12px';
            return '9px 12px';
        }};
        ${({ active, activeColor, label }) => {
            if (
                label === '해약환급금\n일부지급형' ||
                label === '해약환급금\n미지급형' ||
                label === '해약환급금\n일부 ∙ 미지급형'
            )
                return FontStyle(12, 16, active ? '500' : '400', active ? activeColor : '#888888');
            return FontStyle(16, active ? 24 : 22, active ? '500' : '400', active ? activeColor : '#888888');
        }}
    }

    .badge {
        position: absolute;
        display: inline-block;
        top: 0%;
        background: ${({ active, activeSubColor }) => {
            if (active) {
                return activeSubColor;
            }
            return '#f5f5f5';
        }};
        border-radius: 9px;
        width: 26px;
        height: 18px;
        transform: ${({ label }) => {
            switch (label) {
                case '해약환급금\n일부 ∙ 미지급형':
                    return 'translateX(-0.67em)';
                case '해약환급금\n일부지급형':
                    return 'translateX(-0.165em)';
                case '해약환급금\n미지급형':
                    return 'translateX(0.2em)';
                default:
                    return 'translateX(-0.3em)';
            }
        }};
        ${({ active, activeColor }) => {
            return FontStyle(14, 17, '400', active ? activeColor : '#888888', 0);
        }}
    }
`;

const TooltipImage = styled(Image)`
    position: absolute;
    transform: translateX(140px);

    @media (max-width: 340px) {
        transform: translateX(120px);
    }

    z-index: 98;
`;

const GifImage = styled(Image)`
    position: absolute;
    transform: translate(154px, 2px);

    @media (max-width: 340px) {
        transform: translate(134px, 2px);
    }

    z-index: 99;
`;

const CloseImage = styled.div`
    position: absolute;
    width: 18px;
    height: 18px;
    //top: 56px;
    //transform: translateX(98.5px);
    //left: 308px;
    transform: translate(308px, 14px);
    @media (max-width: 340px) {
        transform: translate(288px, 14px);
    }
    cursor: pointer;
    z-index: 99;
`;

SwipeableTabView.displayName = 'SwipeabledTabView';

export default SwipeableTabView;
